import React, { useEffect, useState, useCallback } from 'react';
import Chart from 'chart.js';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Button,
  Form,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle
} from 'reactstrap';

import { buttonStyle } from 'layouts/LoginAutoAuth/components/utils';
import { useForm } from 'react-hook-form';
import ReactLoader from 'components/Loader';
import OpportunityFunnelGraphic from './OpportunityFunnelGraphic';
import { chartOptions, parseOptions } from 'variables/charts.js';

import { useSelector } from 'react-redux';
import CRMDashboardService from '../../service';
import { toast } from 'react-toastify';
import GraphicOpportunitiesByResponsible from './GraphicOpportunitiesByResponsible';
import { useRecordTypesByUser } from 'views/RecordType/hooks/useRecordTypesByUser';
import { entitiesName } from 'views/CRM/shared/utils/entitiesName';
import { toISOFormat, firstDayOfMonth } from '../../shared/dataFormater';
import GraphicOpportunitiesByListCustomFields from './GraphicOpportunitiesByListCustomFields';
import { GraphicOpportunityRevenue } from './GraphicOpportunityRevenue';
import GraphicOpportunitiesWithRevenueByResponsible from './GraphicOpportunitiesWithRevenueByResponsible';
import DefaultDashboardFieldsFilterForm from './form/DefaultFieldsFilterForm';
import { useEntityCustomFieldsByRecordType } from 'views/fieldConfigs/hooks/useCustomFieldsByRecordType';
import CustomFieldsFilterForm from 'views/CRM/shared/components/CustomFieldsFilterForm';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import styled from 'styled-components';
import { customFieldTypes } from 'views/fieldConfigs/utils/customFieldTypes';
import { parseIntFromString } from 'utils/fuctions/parseIntFromString';

if (window.Chart) {
  parseOptions(Chart, chartOptions());
}

const CustomFieldsFilterToggle = styled.div`
  display: inline-block;
  margin-bottom: 1rem;
  user-select: none;
  :hover {
    cursor: pointer;
    text-decoration: underline;
  }
`;
const OpportunityDashboard = () => {
  const [loading, setLoading] = useState(false);
  const { control, handleSubmit, setValue, getValues, register } = useForm();
  const [opportunitiesCountByResponsible, setOpportunitiesCountByResponsible] =
    useState(null);
  const [defaultRecordType, setDefaultRecordType] = useState(null);
  const [opportunitiesByStage, setOpportunitiesByStage] = useState(null);
  const [
    opportunitiesCountByListCustomFields,
    setOpportunitiesCountByListCustomFields
  ] = useState(null);
  const {
    recordTypes: opportunityRecordTypes,
    getRecordTypesByUser: getOpportunityRecordTypesByUser
  } = useRecordTypesByUser(entitiesName.OPPORTUNITY);
  const [total, setTotal] = useState(0);
  const systemConfiguration = useSelector((state) => state.systemConfiguration);
  const [opportunitiesRevenue, setOpportunitiesRevenue] = useState(null);
  const [
    opportunitiesCountByResponsibleWithRevenue,
    setOpportunitiesCountByResponsibleWithRevenue
  ] = useState(null);
  const { customFields, getCustomFields } = useEntityCustomFieldsByRecordType(
    entitiesName.OPPORTUNITY,
    defaultRecordType?.value || defaultRecordType?.id
  );
  const [showCustomFieldsFilters, setShowCustomFieldsFilters] = useState(false);

  useEffect(() => {
    getCustomFields();
  }, [getCustomFields]);

  useEffect(() => {
    setLoading(true);

    if (defaultRecordType) {
      const formatedData = {
        dateBegin: `${toISOFormat(firstDayOfMonth)}`,
        dateEnd: `${toISOFormat(new Date().toLocaleDateString('pt-br'))}`,
        recordType: defaultRecordType ? defaultRecordType.value : ''
      };

      CRMDashboardService.getOpportunitiesCountByStageWithDate(formatedData)
        .then((response) => {
          const listCustomFields = response?.data?.data;
          setTotal(listCustomFields.opportunitiesByStageAmountSum ?? 0);
          setOpportunitiesByStage(listCustomFields.opportunitiesByStage);
          setOpportunitiesCountByResponsible(
            listCustomFields.opportunitiesByResponsible
          );
          setOpportunitiesRevenue(listCustomFields.opportunityRevenue);

          setOpportunitiesCountByListCustomFields(
            response?.data?.data?.opportunitiesByListCustomField
          );

          setOpportunitiesCountByResponsibleWithRevenue(
            listCustomFields.opportunitiesByResponsibleWithRevenue
          );
        })
        .catch((error) => {
          toast.error(error || 'Ops, ocorreu um erro!', {
            autoClose: 3000,
            closeOnClick: true
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [defaultRecordType]);

  const handleSelectRecordType = useCallback(
    (recordTypeId) => {
      setDefaultRecordType(
        opportunityRecordTypes.find((rt) => rt.id === recordTypeId)
      );
    },
    [opportunityRecordTypes]
  );

  useEffect(() => {
    getOpportunityRecordTypesByUser();
  }, [getOpportunityRecordTypesByUser]);

  useEffect(() => {
    if (opportunityRecordTypes !== null) {
      const firstRecordType = opportunityRecordTypes[0] || [];
      setDefaultRecordType({
        label: firstRecordType.name,
        value: firstRecordType.id
      });
    }
  }, [opportunityRecordTypes]);

  if (loading) {
    return (
      <Row className="justify-content-md-center mt-4">
        <ReactLoader />
      </Row>
    );
  }

  const onSubmit = (data) => {
    try {
      let formatedData;

      formatedData = {
        ...data,
        dateBegin: `${toISOFormat(data.dateBegin)}`,
        dateEnd: `${toISOFormat(data.dateEnd)}`,
        filterDateBy: data.filterDateBy,
        recordType: data.selectedRecordType || ''
      };
      if (customFields && showCustomFieldsFilters) {
        customFields.forEach((field) => {
          if (field.type === customFieldTypes.LIST) {
            const possibleValues = field.options.map((value) => value.value);
            if (
              possibleValues.includes(data[field.name]) &&
              data[field.name].length > 0
            ) {
              formatedData[field.name] = data[field.name];
            }
          }
        });
      }

      const currencyFieldsName =
        customFields &&
        customFields
          .filter((field) => field.type === customFieldTypes.CURRENCY)
          .map((field) => field.name);

      formatedData.recordType = defaultRecordType.id || defaultRecordType.value;

      Object.keys(data).forEach((key) => {
        if (currencyFieldsName.includes(key)) {
          if (data[key]) {
            formatedData[key] = parseIntFromString(data[key]);
          }
        }
      });

      CRMDashboardService.getOpportunitiesCountByStageWithDate(formatedData)
        .then((response) => {
          setTotal(response?.data?.data?.opportunitiesByStageAmountSum ?? 0);
          setOpportunitiesByStage(response?.data?.data?.opportunitiesByStage);
          setOpportunitiesCountByResponsible(
            response?.data?.data?.opportunitiesByResponsible
          );
          setOpportunitiesCountByListCustomFields(
            response?.data?.data?.opportunitiesByListCustomField
          );
          setOpportunitiesCountByResponsibleWithRevenue(
            response?.data?.data?.opportunitiesByResponsibleWithRevenue
          );
          setOpportunitiesRevenue(response?.data?.data?.opportunityRevenue);
        })
        .catch((error) => {
          toast.error(error || 'Ops, ocorreu um erro!', {
            autoClose: 3000,
            closeOnClick: true
          });
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (err) {
      toast.error(err || 'Ops, ocorreu um erro!', {
        autoClose: 3000,
        closeOnClick: true
      });
    }
  };

  const SelectRecordTypeDropDown = ({
    recordTypes = [],
    selectedRecordTypeName = '',
    handleSelectRecordType
  }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const toggle = () => setDropdownOpen((prevState) => !prevState);

    return (
      <Dropdown isOpen={dropdownOpen} toggle={toggle}>
        <DropdownToggle
          tag="span"
          className="selected-record-type-name"
          data-toggle="dropdown"
          aria-expanded={dropdownOpen}
        >
          {selectedRecordTypeName}
        </DropdownToggle>
        <DropdownMenu>
          <DropdownItem header>Selecione</DropdownItem>
          {recordTypes.map((rt) => (
            <DropdownItem
              key={rt.id}
              onClick={() => handleSelectRecordType(rt.id)}
            >
              {rt.name}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    );
  };

  return (
    <Container fluid className="pt-4">
      <Card>
        <CardBody>
          {opportunityRecordTypes ? (
            <div>
              <div className="small">
                <span className="font-weight-bold">Tipo de registro: </span>
                <SelectRecordTypeDropDown
                  recordTypes={opportunityRecordTypes}
                  selectedRecordTypeName={
                    defaultRecordType.label || defaultRecordType.name || ''
                  }
                  handleSelectRecordType={handleSelectRecordType}
                />
              </div>
              <Form
                className="needs-validation"
                onSubmit={handleSubmit(onSubmit)}
              >
                <DefaultDashboardFieldsFilterForm
                  setValue={setValue}
                  control={control}
                  getValues={getValues}
                  recordTypeId={defaultRecordType.value || defaultRecordType.id}
                  register={register}
                />

                {customFields && customFields.length > 0 && (
                  <>
                    <CustomFieldsFilterToggle
                      className="small"
                      onClick={() =>
                        setShowCustomFieldsFilters(!showCustomFieldsFilters)
                      }
                    >
                      Filtrar por campos customizados{' '}
                      {showCustomFieldsFilters ? (
                        <FiChevronDown size={18} />
                      ) : (
                        <FiChevronRight size={18} />
                      )}
                    </CustomFieldsFilterToggle>

                    {showCustomFieldsFilters && (
                      <CustomFieldsFilterForm
                        toISOFormat={toISOFormat}
                        getValues={getValues}
                        customFields={customFields}
                        setValue={setValue}
                        control={control}
                      />
                    )}
                  </>
                )}

                <br />

                <Button
                  type="submit"
                  style={buttonStyle(systemConfiguration.primary_color)}
                  className="mb-2"
                >
                  Buscar
                </Button>
              </Form>
              <Row>
                <Col>
                  <Card>
                    <CardHeader>
                      <h6 className="surtitle">Visão Geral</h6>
                      <h5 className="h3 mb-0">
                        Distribuição de Oportunidades por Fase
                      </h5>
                    </CardHeader>
                    <CardBody>
                      <Row>
                        <Col>
                          {opportunitiesByStage && (
                            <OpportunityFunnelGraphic
                              total={total}
                              data={opportunitiesByStage}
                            />
                          )}
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  <Card>
                    <CardHeader>
                      <h6 className="surtitle">Visão Geral</h6>
                      <h5 className="h3 mb-0">
                        Distribuição de Oportunidades por responsável
                      </h5>
                    </CardHeader>
                    <CardBody>
                      <Row>
                        <Col>
                          {opportunitiesCountByResponsible &&
                            opportunitiesCountByResponsible.length > 0 && (
                              <GraphicOpportunitiesByResponsible
                                opportunitiesCountByResponsible={
                                  opportunitiesCountByResponsible
                                }
                              />
                            )}
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  <Card>
                    <CardHeader>
                      <h6 className="surtitle">Visão Geral</h6>
                      <h5 className="h3 mb-0">
                        Distribuição de Oportunidades por campo personalizado
                      </h5>
                    </CardHeader>
                    <CardBody
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(1, 1fr)',
                        justifyContent: 'center'
                      }}
                    >
                      {opportunitiesCountByListCustomFields &&
                        opportunitiesCountByListCustomFields.length &&
                        opportunitiesCountByListCustomFields.map((item) => (
                          <GraphicOpportunitiesByListCustomFields
                            key={item.id}
                            opportunitiesCountByListCustomFields={item}
                          />
                        ))}
                    </CardBody>
                  </Card>
                  <Card>
                    <CardHeader>
                      <h6 className="surtitle">Visão Geral</h6>
                      <h5 className="h3 mb-0">
                        Previsão de receita durante o ano
                      </h5>
                    </CardHeader>
                    <CardBody
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(1, 1fr)',
                        justifyContent: 'center'
                      }}
                    >
                      {opportunitiesRevenue && opportunitiesRevenue.length && (
                        <GraphicOpportunityRevenue
                          data={opportunitiesRevenue}
                        />
                      )}
                    </CardBody>
                  </Card>
                  <Card>
                    <CardHeader>
                      <h6 className="surtitle">Visão Geral</h6>
                      <h5 className="h3 mb-0">Receita total por responsável</h5>
                    </CardHeader>
                    <CardBody
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(1, 1fr)',
                        justifyContent: 'center'
                      }}
                    >
                      {opportunitiesCountByResponsibleWithRevenue &&
                        opportunitiesCountByResponsibleWithRevenue.length && (
                          <GraphicOpportunitiesWithRevenueByResponsible
                            opportunitiesCountByResponsibleWithRevenue={
                              opportunitiesCountByResponsibleWithRevenue
                            }
                          />
                        )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          ) : (
            <div>
              <span className="small">
                Você não tem acesso a nenhum tipo de oportunidade.
              </span>
            </div>
          )}
        </CardBody>
      </Card>
    </Container>
  );
};

export default OpportunityDashboard;
